// The attribute model of the compare page: the product reads of the
// compared products turned into rows in groups, one cell per product. A cell
// is what the sources state, written as the product page writes it, with a
// normalised value beside it (the set of what it states, without the
// wording) that says whether two cells state the same. Pure functions: the
// page draws what they return.
//
// Three rules hold throughout:
// - A row exists only when at least one loaded product has something for it.
// - A cell with nothing is empty: nothing on record, never "no".
// - A product that is not loaded has empty cells that state nothing: they
//   do not count towards difference.

import {
  CapabilityClaim,
  ClaimPart,
  EntityRef,
  FactSource,
  ProductDetail,
  ProductLink,
  ProductLinkGroup,
  ProductSpecification,
  ProductStructureLink,
  SpecificationStatement,
  Stance,
} from "../../types/productPlane";
import {
  codeLabel,
  codeWords,
  dateRange,
  formatNumber,
  formatFigure,
  formatQuantity,
  formatSpecificationValue,
} from "../Detail/format";
import {
  DisplayUnit,
  displayUnit,
  statementNumbers,
} from "../Detail/unitScale";
import { ExportTable } from "../../utils/export/table";
import { coreOf, groupName } from "../Detail/claimGroups";
import { ROLE_ORDER, otherLabel } from "../ProductDetail/productRelations";

export type CompareGroupId =
  | "identity"
  | "specifications"
  | "standards"
  | "capabilities"
  | "involved"
  | "record";

// One span of a cell. The spans of a cell are written one after the other:
// each brings its own spaces and separators.
export type CellPart =
  // Plain words
  | { kind: "text"; text: string }
  // A figure or a date, in the mono face
  | { kind: "mono"; text: string }
  // Quieter words: a separator, a qualifier, a condition
  | { kind: "dim"; text: string }
  // The name of an entity, which opens it
  | { kind: "link"; text: string; entity: { id: string; type: string } }
  // The mark of a stance; `text` is the stance in words
  | { kind: "stance"; stance: Stance; text: string }
  // A small chip: a maturity, or, in the warning tone, a thin record
  | { kind: "chip"; text: string; tone?: "warning" }
  // The amber mark of sources that disagree, with the other values
  | { kind: "disagreement"; text: string };

export interface CompareCell {
  // What the cell shows, in order; empty for an empty cell
  parts: CellPart[];
  // What the cell states, without wording or order: two cells state the
  // same when their values are equal. Empty for an empty cell.
  value: string;
  // The documents behind the facts of the cell, each once
  sources: FactSource[];
  // True when the product has nothing on record for the row
  empty: boolean;
  // False for a product that is not loaded: its cell states nothing
  loaded: boolean;
  // True when the cell does not state what the reference's cell states.
  // False in the reference's own column, in a row that is not compared,
  // and everywhere while the reference is not loaded.
  differsFromReference: boolean;
}

export interface CompareRow {
  // Stable for the row whatever the products: "spec:weight|kg"
  key: string;
  label: string;
  // What stands quieter beside the label: a unit, "curated"
  sublabel: string | null;
  // One cell per product, in the order of the columns
  cells: CompareCell[];
  // True when the loaded products do not all state the same; an empty cell
  // is a statement of its own. Always false in a group that is not compared.
  differs: boolean;
}

export interface CompareGroup {
  id: CompareGroupId;
  label: string;
  // What the head of the group says of its rows; null for nothing
  note: string | null;
  // False for the group about the record itself (how many articles, from
  // when): the evidence behind a product is no property of it, so its rows
  // are neither marked nor counted nor hidden as "the same"
  compared: boolean;
  rows: CompareRow[];
}

// One column: the id the address names, and the product read for it; null
// while it loads and when the read failed
export interface CompareColumn {
  id: string;
  product: ProductDetail | null;
}

// Whether a cell carries the amber marks of a record in doubt: the other
// values the sources of a specification give, and a product on record in
// one article only. Hidden for now: the page shows the values alone.
export const SHOW_WARNINGS = false;

// The maturity a claim gives when no source states one: no maturity
const UNKNOWN_MATURITY = "unknown";

// ── Parts ─────────────────────────────────────────────────────────────

const text = (value: string): CellPart => ({ kind: "text", text: value });
const mono = (value: string): CellPart => ({ kind: "mono", text: value });
const dim = (value: string): CellPart => ({ kind: "dim", text: value });

// What an entity without a name reads, as its link reads on a detail page
const refName = (ref: EntityRef): string =>
  ref.name ?? `Unnamed ${codeWords(ref.type)}`;

const link = (ref: EntityRef): CellPart => ({
  kind: "link",
  text: refName(ref),
  entity: { id: ref.id, type: ref.type },
});

const DOT = " · ";

// Lists of parts as one, with a quiet separator between them
const joined = (lists: CellPart[][], separator = DOT): CellPart[] =>
  lists.flatMap((list, index) =>
    index === 0 ? list : [dim(separator), ...list],
  );

// What a cell is made of before it is set against the others; null for
// nothing on record
interface Content {
  parts: CellPart[];
  value: string;
  sources: FactSource[];
}

// The documents of several facts, each once, in the order first named
const mergeSources = (lists: FactSource[][]): FactSource[] => {
  const seen = new Set<string>();
  return lists.flat().filter((source) => {
    if (seen.has(source.document_id)) {
      return false;
    }
    seen.add(source.document_id);
    return true;
  });
};

// A set of values as one value: each once, whatever the order
const setValue = (values: string[]): string =>
  Array.from(new Set(values)).sort().join("|");

const byText = (left: string, right: string): number =>
  left.localeCompare(right);

// A row before its cells: how one product fills it
interface RowSpec {
  key: string;
  label: string;
  sublabel?: string | null;
  fill: (product: ProductDetail) => Content | null;
}

// ── Identity ──────────────────────────────────────────────────────────

// A field that is one code or one text, with no source of its own
const plain = (value: string | null, shown?: string): Content | null =>
  value ? { parts: [text(shown ?? value)], value, sources: [] } : null;

const statusContent = (product: ProductDetail): Content | null => {
  const { status } = product;
  if (!status) {
    return null;
  }
  return {
    parts: [
      text(codeWords(status.status)),
      ...(status.since ? [dim(" since "), mono(status.since)] : []),
      ...(status.until ? [dim(" until "), mono(status.until)] : []),
      ...(status.wording ? [dim(DOT), text(status.wording)] : []),
    ],
    // The article's wording is how it is said, not what is said
    value: [status.status, status.since ?? "", status.until ?? ""].join("|"),
    sources: [],
  };
};

const IDENTITY_ROWS: RowSpec[] = [
  {
    key: "identity:category",
    label: "Category",
    sublabel: "curated",
    fill: (product) =>
      product.categories.length > 0
        ? {
            parts: joined(
              product.categories.map((entry) => [link(entry.category)]),
            ),
            value: setValue(product.categories.map((entry) => entry.category.id)),
            sources: mergeSources(
              product.categories.map((entry) => entry.sources),
            ),
          }
        : null,
  },
  {
    key: "identity:category-stated",
    label: "Category",
    sublabel: "as stated",
    fill: (product) => plain(product.category_stated),
  },
  {
    key: "identity:level",
    label: "Level",
    fill: (product) => plain(product.level, codeWords(product.level)),
  },
  { key: "identity:status", label: "Status", fill: statusContent },
  {
    key: "identity:integration",
    label: "Integration",
    // One the documents leave unclear is nothing on record, as on the
    // product page
    fill: (product) =>
      product.integration === "unclear"
        ? null
        : plain(product.integration, codeWords(product.integration)),
  },
  {
    key: "identity:hosts",
    label: "Typical hosts",
    fill: (product) =>
      product.typical_hosts.length > 0
        ? {
            parts: joined(
              product.typical_hosts.map((host) => [link(host.category)]),
            ),
            value: setValue(
              product.typical_hosts.map((host) => host.category.id),
            ),
            sources: mergeSources(
              product.typical_hosts.map((host) => host.sources),
            ),
          }
        : null,
  },
];

// ── Specifications ────────────────────────────────────────────────────

const isNumeric = (statement: SpecificationStatement): boolean =>
  statement.value_min !== null || statement.value_max !== null;

// What a statement states, without its wording: the range, or the text
const statementValue = (statement: SpecificationStatement): string =>
  isNumeric(statement)
    ? `${statement.value_min ?? ""}..${statement.value_max ?? ""}`
    : (statement.value_text ?? "").trim().toLowerCase();

// The value in the row's unit: "0.9 kg", "10 – 12 kg"
const valueInUnit = (
  statement: SpecificationStatement,
  display: DisplayUnit,
): string =>
  [formatSpecificationValue(statement, display.divisor), display.unit]
    .filter(Boolean)
    .join(" ");

// A statement as the document words it; without a wording, its value
const statementWords = (
  statement: SpecificationStatement,
  display: DisplayUnit,
): string => statement.wording ?? valueInUnit(statement, display);

// A numeric statement normalised to the row's unit, its qualifier as a
// sign: "≈ 20 – 50 kg", "≥ 6", "≤ 15.24 km"
const normalisedValue = (
  statement: SpecificationStatement,
  display: DisplayUnit,
): string => {
  const { value_min: low, value_max: high, qualifier } = statement;
  const approx = qualifier === "approx" ? "≈ " : "";
  const figureOf = (value: number) => formatFigure(value / display.divisor);
  let figure: string;
  if (low !== null && high !== null && figureOf(low) !== figureOf(high)) {
    figure = `${approx}${figureOf(low)} – ${figureOf(high)}`;
  } else {
    const sign =
      high === null || qualifier === "at_least"
        ? "≥ "
        : low === null || qualifier === "up_to"
          ? "≤ "
          : approx;
    figure = `${sign}${figureOf((low ?? high) as number)}`;
  }
  return [figure, display.unit].filter(Boolean).join(" ");
};

// A statement as the cell shows it: a number normalised to the row's unit,
// in the mono face, or the text it states
const statementPart = (
  statement: SpecificationStatement,
  display: DisplayUnit,
): CellPart =>
  isNumeric(statement)
    ? mono(normalisedValue(statement, display))
    : text((statement.value_text ?? statement.wording ?? "").trim());

const specificationContent = (
  specification: ProductSpecification,
  display: DisplayUnit,
): Content | null => {
  const [newest, ...earlier] = specification.statements;
  if (!newest) {
    return null;
  }
  const parts: CellPart[] = [statementPart(newest, display)];
  if (newest.conditions) {
    parts.push(dim(` ${newest.conditions}`));
  }
  if (SHOW_WARNINGS && specification.disagreement) {
    // The other values the sources give, each once
    const others = new Map<string, string>();
    for (const statement of earlier) {
      const value = statementValue(statement);
      if (value !== statementValue(newest) && !others.has(value)) {
        others.set(value, statementWords(statement, display));
      }
    }
    parts.push({
      kind: "disagreement",
      text:
        others.size > 0
          ? `${Array.from(others.values()).join(", ")} elsewhere`
          : "the sources disagree",
    });
  }
  return {
    parts,
    value: setValue(specification.statements.map(statementValue)),
    sources: mergeSources(
      specification.statements.map((statement) => statement.sources),
    ),
  };
};

// One row per key and unit any product states, by key: the same key in two
// units is two rows, since the values cannot be set side by side. A row
// reads in one unit for every product, the one its values read best in
// (250000 m: 250 km)
const specificationRows = (products: ProductDetail[]): RowSpec[] => {
  const found = new Map<
    string,
    { key: string; unit: string | null; values: number[] }
  >();
  for (const { key, unit, statements } of products.flatMap(
    (product) => product.specifications,
  )) {
    const id = `${key}|${unit ?? ""}`;
    const entry = found.get(id) ?? { key, unit, values: [] };
    entry.values.push(...statementNumbers(statements));
    found.set(id, entry);
  }
  return Array.from(found.entries())
    .sort(
      ([, left], [, right]) =>
        byText(left.key, right.key) ||
        byText(left.unit ?? "", right.unit ?? ""),
    )
    .map(([id, { key, unit, values }]) => {
      const display = displayUnit(unit, values);
      return {
        key: `spec:${id}`,
        label: codeLabel(key),
        sublabel: display.unit,
        fill: (product: ProductDetail) => {
          const specification = product.specifications.find(
            (entry) => entry.key === key && (entry.unit ?? "") === (unit ?? ""),
          );
          return specification
            ? specificationContent(specification, display)
            : null;
        },
      };
    });
};

// ── Standards ─────────────────────────────────────────────────────────

// What a standard named without a status reads, and counts as
const NO_STATUS = "named, no status stated";

const standardRows = (products: ProductDetail[]): RowSpec[] => {
  const names = Array.from(
    new Set(
      products.flatMap((product) =>
        product.standards.map((entry) => entry.standard),
      ),
    ),
  ).sort(byText);

  return names.map(
    (name): RowSpec => ({
      key: `standard:${name}`,
      label: name,
      fill: (product) => {
        const entries = product.standards.filter(
          (entry) => entry.standard === name,
        );
        if (entries.length === 0) {
          return null;
        }
        // One entry per document: the same standing is written once
        const standings = new Map<string, CellPart[]>();
        for (const entry of entries) {
          const id = `${entry.status ?? ""}@${entry.as_of ?? ""}`;
          standings.set(id, [
            entry.status ? text(codeWords(entry.status)) : dim(NO_STATUS),
            ...(entry.as_of ? [dim(" as of "), mono(entry.as_of)] : []),
          ]);
        }
        return {
          parts: joined(Array.from(standings.values())),
          value: setValue(entries.map((entry) => entry.status ?? NO_STATUS)),
          sources: mergeSources(entries.map((entry) => entry.sources)),
        };
      },
    }),
  );
};

// ── Capabilities ──────────────────────────────────────────────────────

// The strongest stance first: what a product has says more of it than what
// it is stated to lack
const STANCE_ORDER: Stance[] = ["has", "enables", "requires", "lacks"];

// A stance in words, beside its mark
export const STANCE_WORDS: Record<Stance, string> = {
  has: "has",
  enables: "enables",
  requires: "stated as required",
  lacks: "stated as lacking",
};

// The name of a claim's row: its abilities as the product page names its
// group, or, for a claim with none, the capability itself
const coreLabel = (claim: CapabilityClaim): string =>
  claim.capability.abilities.length > 0
    ? groupName(claim.capability)
    : (claim.capability.capability.name ?? claim.capability.capability.id);

const partNames = (parts: ClaimPart[]): string =>
  parts.map((entry) => entry.part.name ?? entry.part.id).join(", ");

const partIds = (parts: ClaimPart[]): string =>
  parts
    .map((entry) => entry.part.id)
    .sort()
    .join(",");

// The maturity a claim states; null for none and for "unknown"
const statedMaturity = (claim: CapabilityClaim): string | null =>
  claim.maturity && claim.maturity !== UNKNOWN_MATURITY ? claim.maturity : null;

// What one claim states beside its stance: what the ability acts on, the
// conditions, the modes, the means, the maturity and the product that
// provides the means; nothing for a claim that states nothing more
const claimDetail = (claim: CapabilityClaim): CellPart[] => {
  const { objects, conditions, modes, means } = claim.capability;
  const stated = joined([
    ...(objects.length > 0 ? [[text(partNames(objects))]] : []),
    ...(conditions.length > 0 ? [[text(partNames(conditions))]] : []),
    ...(modes.length > 0 ? [[dim("mode "), text(partNames(modes))]] : []),
    ...(means.length > 0 ? [[dim("with "), text(partNames(means))]] : []),
  ]);
  const maturity = statedMaturity(claim);
  return [
    ...stated,
    ...(maturity ? [{ kind: "chip", text: codeWords(maturity) } as CellPart] : []),
    ...(claim.means_product
      ? [
          dim(stated.length > 0 || maturity ? " · by " : "by "),
          link(claim.means_product),
        ]
      : []),
  ];
};

// The same, as ids and codes
const claimValue = (claim: CapabilityClaim): string => {
  const { objects, conditions, modes, means } = claim.capability;
  return [
    partIds(objects),
    partIds(conditions),
    partIds(modes),
    partIds(means),
    statedMaturity(claim) ?? "",
    claim.means_product?.id ?? "",
  ].join("/");
};

// What the claims of one stance state, each statement once, with how many
// claims make it where more than one does; the claims that state nothing
// beyond the stance are left to the stance's mark
const claimDetails = (claims: CapabilityClaim[]): CellPart[][] => {
  const stated = new Map<string, { parts: CellPart[]; count: number }>();
  for (const claim of claims) {
    const parts = claimDetail(claim);
    if (parts.length === 0) {
      continue;
    }
    const key = JSON.stringify(parts);
    const known = stated.get(key);
    if (known) {
      known.count += 1;
    } else {
      stated.set(key, { parts, count: 1 });
    }
  }
  return Array.from(stated.values()).map(({ parts, count }) =>
    count > 1 ? [...parts, dim(` ×${count}`)] : parts,
  );
};

// A product's claims on one ability: the strongest stance among them, with
// what the claims of that stance state
const capabilityContent = (claims: CapabilityClaim[]): Content | null => {
  const stance = STANCE_ORDER.find((candidate) =>
    claims.some((claim) => claim.stance === candidate),
  );
  if (!stance) {
    return null;
  }
  const strongest = claims.filter((claim) => claim.stance === stance);
  const details = claimDetails(strongest);
  return {
    parts: [
      { kind: "stance", stance, text: STANCE_WORDS[stance] },
      ...(details.length > 0
        ? [stance === "has" ? text(" ") : dim(DOT), ...joined(details, " ; ")]
        : []),
    ],
    value: `${stance}#${setValue(strongest.map(claimValue))}`,
    sources: mergeSources(strongest.map((claim) => claim.sources)),
  };
};

// One row per core any product has a claim on, the core as the product page
// groups by it (coreOf): the uncurated abilities of one name are one row. By
// effect area and then by name; a core under no effect area comes last
const capabilityRows = (products: ProductDetail[]): RowSpec[] => {
  const found = new Map<string, { label: string; area: string | null }>();
  for (const claim of products.flatMap((product) => product.claims)) {
    const core = coreOf(claim);
    if (!found.has(core)) {
      found.set(core, {
        label: coreLabel(claim),
        area: claim.capability.effect_area?.name ?? null,
      });
    }
  }
  return Array.from(found.entries())
    .sort(([, left], [, right]) => {
      if (left.area !== right.area) {
        if (left.area === null || right.area === null) {
          return left.area === null ? 1 : -1;
        }
        return byText(left.area, right.area);
      }
      return byText(left.label, right.label);
    })
    .map(([core, { label }]) => ({
      key: `capability:${core}`,
      label,
      fill: (product) =>
        capabilityContent(product.claims.filter((claim) => coreOf(claim) === core)),
    }));
};

// ── Who is involved ───────────────────────────────────────────────────

// A stated number of units after a name: the number in the mono face, then
// the document's words where they say more than the number does
const quantityParts = (
  quantity: number | null,
  wording: string | null,
): CellPart[] => {
  const words = formatQuantity(quantity, wording);
  if (!words) {
    return [];
  }
  if (quantity === null) {
    return [text(` ${words}`)];
  }
  const number = formatNumber(quantity);
  const rest = words.slice(number.length);
  return [text(" "), mono(number), ...(rest ? [text(rest)] : [])];
};

const roleRows = (products: ProductDetail[]): RowSpec[] => {
  const present = new Set<string>(
    products.flatMap((product) =>
      product.organizations.map((group) => group.role),
    ),
  );
  // A role the list does not know yet comes last
  const roles = [
    ...ROLE_ORDER.filter((role) => present.has(role)),
    ...Array.from(present).filter(
      (role) => !(ROLE_ORDER as string[]).includes(role),
    ),
  ];
  return roles.map(
    (role): RowSpec => ({
      key: `organization:${role}`,
      label: codeLabel(role),
      fill: (product) => {
        const items = product.organizations
          .filter((group) => group.role === role)
          .flatMap((group) => group.items);
        if (items.length === 0) {
          return null;
        }
        // "other" names no role: the relation says what the organisation is
        // to the product
        const named = role !== "other";
        return {
          parts: joined(
            items.map((item) => [
              link(item.organization),
              ...(named ? [] : [dim(` ${codeWords(item.relation)}`)]),
              ...quantityParts(item.quantity, item.quantity_wording),
            ]),
          ),
          value: setValue(
            items.map((item) =>
              [
                item.organization.id,
                named ? "" : item.relation,
                item.quantity ?? item.quantity_wording ?? "",
              ].join("/"),
            ),
          ),
          sources: mergeSources(items.map((item) => item.sources)),
        };
      },
    }),
  );
};

const linkContent = (items: ProductLink[]): Content | null =>
  items.length > 0
    ? {
        parts: joined(
          items.map((item) => [
            link(item.entity),
            ...quantityParts(item.quantity, item.quantity_wording),
          ]),
        ),
        value: setValue(
          items.map((item) =>
            [item.entity.id, item.quantity ?? item.quantity_wording ?? ""].join(
              "/",
            ),
          ),
        ),
        sources: mergeSources(items.map((item) => item.sources)),
      }
    : null;

// One row per relation the products have to a programme, or to a facility,
// by the relation's label
const linkRows = (
  products: ProductDetail[],
  kind: "project" | "facility",
  groupsOf: (product: ProductDetail) => ProductLinkGroup[],
): RowSpec[] =>
  Array.from(
    new Set(
      products.flatMap((product) =>
        groupsOf(product).map((group) => group.relation),
      ),
    ),
  )
    .sort((left, right) => byText(codeLabel(left), codeLabel(right)))
    .map((relation) => ({
      key: `${kind}:${relation}`,
      label: codeLabel(relation),
      fill: (product) =>
        linkContent(
          groupsOf(product)
            .filter((group) => group.relation === relation)
            .flatMap((group) => group.items),
        ),
    }));

// The incidents of a product, each once with every way it is linked: the
// count, then the names
const incidentContent = (product: ProductDetail): Content | null => {
  const incidents = new Map<
    string,
    { entity: EntityRef; relations: string[]; sources: FactSource[][] }
  >();
  for (const group of product.incidents) {
    for (const item of group.items) {
      const held = incidents.get(item.entity.id) ?? {
        entity: item.entity,
        relations: [],
        sources: [],
      };
      held.relations.push(group.relation);
      held.sources.push(item.sources);
      incidents.set(item.entity.id, held);
    }
  }
  if (incidents.size === 0) {
    return null;
  }
  const entries = Array.from(incidents.values());
  return {
    parts: [
      mono(formatNumber(entries.length)),
      dim(DOT),
      ...joined(
        entries.map((entry) => [
          link(entry.entity),
          dim(` ${entry.relations.map(codeWords).join(", ")}`),
        ]),
      ),
    ],
    value: setValue(
      entries.map(
        (entry) => `${entry.entity.id}/${[...entry.relations].sort().join(",")}`,
      ),
    ),
    sources: mergeSources(entries.flatMap((entry) => entry.sources)),
  };
};

// The products of one structure relation; the role of a component is the
// document's wording, so the value is the products alone
const structureContent = (links: ProductStructureLink[]): Content | null =>
  links.length > 0
    ? {
        parts: joined(
          links.map((entry) => [
            link(entry.product),
            ...(entry.role ? [dim(` ${entry.role}`)] : []),
          ]),
        ),
        value: setValue(links.map((entry) => entry.product.id)),
        sources: mergeSources(links.map((entry) => entry.sources)),
      }
    : null;

const STRUCTURE_ROWS: RowSpec[] = [
  {
    key: "structure:contains",
    label: "Components",
    fill: (product) => structureContent(product.structure.contains),
  },
  {
    key: "structure:built_into",
    label: "Built into",
    fill: (product) => structureContent(product.structure.built_into),
  },
  {
    key: "structure:variant_of",
    label: "Variant of",
    fill: (product) => structureContent(product.structure.variant_of),
  },
  {
    key: "structure:variants",
    label: "Variants",
    fill: (product) => structureContent(product.structure.variants),
  },
];

// The links that are neither a component nor a variant, worded as the
// product page words them: "Uses", "Used by"
const otherStructureRows = (products: ProductDetail[]): RowSpec[] =>
  Array.from(
    new Set(
      products.flatMap((product) => product.structure.other.map(otherLabel)),
    ),
  )
    .sort(byText)
    .map((label) => ({
      key: `structure:other:${label}`,
      label,
      fill: (product) =>
        structureContent(
          product.structure.other.filter((entry) => otherLabel(entry) === label),
        ),
    }));

const involvedRows = (products: ProductDetail[]): RowSpec[] => [
  ...roleRows(products),
  ...linkRows(products, "project", (product) => product.projects),
  ...linkRows(products, "facility", (product) => product.facilities),
  { key: "incidents", label: "Incidents", fill: incidentContent },
  ...STRUCTURE_ROWS,
  ...otherStructureRows(products),
];

// ── On record ─────────────────────────────────────────────────────────

// The sources of every fact of a product that can carry sources
const factSources = (product: ProductDetail): FactSource[][] => {
  const { structure } = product;
  return [
    ...product.categories,
    ...product.typical_hosts,
    ...product.organizations.flatMap((group) => group.items),
    ...product.specifications.flatMap(
      (specification) => specification.statements,
    ),
    ...product.standards,
    ...product.claims,
    ...[...product.projects, ...product.incidents, ...product.facilities].flatMap(
      (group) => group.items,
    ),
    ...structure.contains,
    ...structure.built_into,
    ...structure.variant_of,
    ...structure.variants,
    ...structure.other,
  ].map((fact) => fact.sources);
};

/**
 * The share of a product's facts that rest on one document.
 *
 * Counted over every fact that carries a source: its categories, typical
 * hosts, organisation relations, specification statements, standards,
 * claims, links to programmes, incidents and facilities, and
 * links to other products. A document a fact names twice counts once.
 *
 * @param product - The product read
 * @returns The share as a whole percentage; null when the product has no
 *   fact with a source
 */
export const singleSourceShare = (product: ProductDetail): number | null => {
  const documents = factSources(product)
    .map((sources) => new Set(sources.map((source) => source.document_id)).size)
    .filter((count) => count > 0);
  if (documents.length === 0) {
    return null;
  }
  const single = documents.filter((count) => count === 1).length;
  return Math.round((single / documents.length) * 100);
};

const RECORD_ROWS: RowSpec[] = [
  {
    key: "record:articles",
    label: "Articles",
    fill: (product) => ({
      parts: [
        mono(formatNumber(product.source_count)),
        ...(SHOW_WARNINGS && product.source_count === 1
          ? [
              {
                kind: "chip",
                text: "single source",
                tone: "warning",
              } as CellPart,
            ]
          : []),
      ],
      value: String(product.source_count),
      sources: [],
    }),
  },
  {
    key: "record:latest",
    label: "Latest article",
    fill: (product) => {
      const latest = dateRange(
        product.sources.items.map((source) => source.date),
      )?.latest;
      return latest
        ? { parts: [mono(latest)], value: latest, sources: [] }
        : null;
    },
  },
  {
    key: "record:single-source",
    label: "Facts with one source",
    fill: (product) => {
      const share = singleSourceShare(product);
      return share === null
        ? null
        : { parts: [mono(`${share}%`)], value: `${share}%`, sources: [] };
    },
  },
];

// ── The model ─────────────────────────────────────────────────────────

// A row from its spec: one cell per column, set against the others and
// against the reference
const toRow = (
  spec: RowSpec,
  columns: CompareColumn[],
  referenceIndex: number,
  compared: boolean,
): CompareRow => {
  const contents = columns.map((column) =>
    column.product ? spec.fill(column.product) : null,
  );
  const values = contents.map((content) => content?.value ?? "");
  const loadedValues = values.filter((_value, index) => columns[index].product);
  const reference =
    compared && columns[referenceIndex]?.product
      ? values[referenceIndex]
      : null;

  return {
    key: spec.key,
    label: spec.label,
    sublabel: spec.sublabel ?? null,
    differs: compared && new Set(loadedValues).size > 1,
    cells: contents.map((content, index) => {
      const loaded = columns[index].product !== null;
      return {
        parts: content?.parts ?? [],
        value: values[index],
        sources: content?.sources ?? [],
        empty: content === null,
        loaded,
        differsFromReference:
          loaded &&
          reference !== null &&
          index !== referenceIndex &&
          values[index] !== reference,
      };
    }),
  };
};

/**
 * The comparison of some products: rows in groups, one cell per product.
 *
 * @param columns - The compared products in the order of the columns, each
 *   with its read; a read that is null (loading, failed) gives empty cells
 * @param referenceId - The id of the column the others are set against
 * @returns The groups that have a row, in the order of the page; a row is
 *   left out when no loaded product has anything for it
 */
export const buildCompareModel = (
  columns: CompareColumn[],
  referenceId: string | null,
): CompareGroup[] => {
  const products = columns.flatMap((column) =>
    column.product ? [column.product] : [],
  );
  const referenceIndex = columns.findIndex(
    (column) => column.id === referenceId,
  );

  const groups: (Omit<CompareGroup, "rows"> & { specs: RowSpec[] })[] = [
    {
      id: "identity",
      label: "Identity",
      note: null,
      compared: true,
      specs: IDENTITY_ROWS,
    },
    {
      id: "specifications",
      label: "Specifications",
      note: "the newest statement, in the row's unit",
      compared: true,
      specs: specificationRows(products),
    },
    {
      id: "standards",
      label: "Standards",
      note: null,
      compared: true,
      specs: standardRows(products),
    },
    {
      id: "capabilities",
      label: "Capabilities",
      note: "as claimed, with the conditions and modes stated",
      compared: true,
      specs: capabilityRows(products),
    },
    {
      id: "involved",
      label: "Who is involved",
      note: null,
      compared: true,
      specs: involvedRows(products),
    },
    {
      id: "record",
      label: "On record",
      note: "the evidence behind each product; not compared",
      compared: false,
      specs: RECORD_ROWS,
    },
  ];

  return groups
    .map(({ specs, ...group }) => ({
      ...group,
      rows: specs
        .map((spec) => toRow(spec, columns, referenceIndex, group.compared))
        .filter((row) => row.cells.some((cell) => !cell.empty)),
    }))
    .filter((group) => group.rows.length > 0);
};

// How many of the rows compared differ: "31 of 38 rows differ"
export const countDifferences = (
  groups: CompareGroup[],
): { differing: number; total: number } => {
  const rows = groups
    .filter((group) => group.compared)
    .flatMap((group) => group.rows);
  return {
    differing: rows.filter((row) => row.differs).length,
    total: rows.length,
  };
};

// The groups as "differences only" shows them: without the rows that are
// the same for every product, and without a group left empty. A group that
// is not compared is kept whole.
export const visibleGroups = (
  groups: CompareGroup[],
  differencesOnly: boolean,
): CompareGroup[] =>
  differencesOnly
    ? groups
        .map((group) =>
          group.compared
            ? { ...group, rows: group.rows.filter((row) => row.differs) }
            : group,
        )
        .filter((group) => group.rows.length > 0)
    : groups;

const partText = (part: CellPart): string => {
  switch (part.kind) {
    case "chip":
      return ` (${part.text})`;
    case "disagreement":
      return ` ⚠ ${part.text}`;
    default:
      return part.text;
  }
};

// A cell as plain text, its spaces single; empty for an empty cell
export const cellText = (cell: CompareCell): string =>
  cell.parts.map(partText).join("").replace(/\s+/g, " ").trim();

// A row's label with what stands beside it: "Weight · kg"
export const rowLabel = (row: CompareRow): string =>
  row.sublabel ? `${row.label}${DOT}${row.sublabel}` : row.label;

/**
 * The rows as an export table: the attribute with its group, then a column
 * per product.
 *
 * @param groups - The groups to write: the ones the page shows
 * @param names - The products' names, in the order of the columns
 * @returns The table, a header per column; an empty cell is empty
 */
export const tableOfCompare = (
  groups: CompareGroup[],
  names: string[],
): ExportTable => ({
  name: "Comparison",
  columns: [
    { header: "attribute", width: 40 },
    ...names.map((header) => ({ header, width: 30 })),
  ],
  rows: groups.flatMap((group) =>
    group.rows.map((row) => [
      `${group.label}${DOT}${rowLabel(row)}`,
      ...row.cells.map(cellText),
    ]),
  ),
});
